/*
import { useEffect } from "react";

/**
 * SEO: injects <title>, meta, OG, Twitter, canonical, robots, and JSON-LD.
 *
 * Props:
 *  - title, description, canonical
 *  - og: { title, description, url, type, image }
 *  - twitter: { card, title, description, image, site, creator }
 *  - robots: "index,follow" | "noindex,follow" | etc
 *  - jsonLd: object | object[]  (single or array of JSON-LD blocks)
 */
/*
export default function SEO({
  title,
  description,
  canonical,
  og = {},
  twitter = {},
  robots,
  jsonLd,
}) {
  useEffect(() => {
    // title
    if (title) document.title = title;

    // helper to upsert a tag
    const upsert = (selector, elBuilder) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = elBuilder();
        document.head.appendChild(el);
      }
      return el;
    };

    // description
    if (description) {
      const el = upsert('meta[name="description"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        return m;
      });
      el.setAttribute("content", description);
    }

    // canonical
    if (canonical) {
      const el = upsert('link[rel="canonical"]', () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
      });
      el.setAttribute("href", canonical);
    }

    // robots
    if (robots) {
      const el = upsert('meta[name="robots"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "robots");
        return m;
      });
      el.setAttribute("content", robots);
    }

    // Open Graph
    const setOG = (p, v) => {
      if (!v) return;
      const el = upsert(`meta[property="og:${p}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute("property", `og:${p}`);
        return m;
      });
      el.setAttribute("content", v);
    };
    setOG("title", og.title || title);
    setOG("description", og.description || description);
    setOG("url", og.url || canonical);
    setOG("type", og.type || "website");
    if (og.image) setOG("image", og.image);

    // Twitter Card
    const setTW = (n, v) => {
      if (!v) return;
      const el = upsert(`meta[name="twitter:${n}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute("name", `twitter:${n}`);
        return m;
      });
      el.setAttribute("content", v);
    };
    setTW("card", twitter.card || "summary_large_image");
    setTW("title", twitter.title || title);
    setTW("description", twitter.description || description);
    if (twitter.image || og.image) setTW("image", twitter.image || og.image);
    if (twitter.site) setTW("site", twitter.site);
    if (twitter.creator) setTW("creator", twitter.creator);

    // JSON-LD (allow single object or array)
    const blocks = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    const scriptEls = [];
    for (const block of blocks) {
      const scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.text = JSON.stringify(block);
      document.head.appendChild(scriptEl);
      scriptEls.push(scriptEl);
    }

    // cleanup JSON-LD on unmount
    return () => {
      for (const el of scriptEls) {
        try { document.head.removeChild(el); } catch {}
      }
    };
  }, [title, description, canonical, og, twitter, robots, jsonLd]);

  return null;
}

*/
// src/components/SEO.jsx
import { useEffect } from "react";

/** upsert a <meta> by name or property */
function setMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

/** ensure a single canonical <link> */
function setCanonical(href) {
  if (!href) return;
  let ln = document.head.querySelector('link[rel="canonical"]');
  if (!ln) {
    ln = document.createElement("link");
    ln.setAttribute("rel", "canonical");
    document.head.appendChild(ln);
  }
  ln.setAttribute("href", href);
}

/** inject/replace a JSON-LD <script> block */
function setJsonLd(id, data) {
  const tagId = `jsonld-${id}`;
  let el = document.getElementById(tagId);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = tagId;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SEO({
  title,
  description,
  canonical,
  og = {},
  twitter = {},
  jsonLd,           // object
  robots = "index,follow",
  siteName = "We Are Local",
}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta("name", "description", description);
    if (robots) setMeta("name", "robots", robots);
    if (canonical) setCanonical(canonical);

    // Open Graph
    setMeta("property", "og:type", og.type || "article");
    setMeta("property", "og:title", og.title || title);
    setMeta("property", "og:description", og.description || description);
    setMeta("property", "og:url", og.url || canonical);
    setMeta("property", "og:image", og.image);
    setMeta("property", "og:site_name", siteName);

    // Twitter
    setMeta("name", "twitter:card", twitter.card || "summary_large_image");
    setMeta("name", "twitter:title", twitter.title || title);
    setMeta("name", "twitter:description", twitter.description || description);
    setMeta("name", "twitter:image", twitter.image || og.image);

    // JSON-LD
 //   if (jsonLd) setJsonLd("article", jsonLd);
  //


  if (Array.isArray(jsonLd)) {
  jsonLd.forEach((block, i) => {
    setJsonLd(`block-${i}`, block);
  });
} else if (jsonLd) {
  setJsonLd("article", jsonLd);
}
}, [title, description, canonical, JSON.stringify(og), JSON.stringify(twitter), JSON.stringify(jsonLd), robots, siteName]);

  return null;
}